<template>
  <div class="p-notes-tab py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
          <p-search-input
            v-model="search"
            :items="noteTitles"
        ></p-search-input>
        </div>
        <div v-if="hasAccess()" class="p-toolbar-item p-right">
          <v-btn
            class="p-btn-rounded ml-4 mr-1"
            large
            rounded
            @click.stop="openAddDialog"
          >
            {{ $t('note.createNote') }}
          </v-btn>
        </div>
      </div>
      <v-row>
        <v-col v-if="notes.length > 0">
          <v-data-table
            :headers="tableHeaders"
            :items="notesComputed"
            :search="search"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            class="p-no-borders"
            hide-default-footer
            @pagination="setPagination"
          >
            <template v-slot:[`item.changed`]="{ item }">
              <v-tooltip v-if="item.changed" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <span v-if="item.changed" class="item-changed"></span>
                  </v-btn>
                </template>
                {{ $t('note.table.changedTooltip') }}
              </v-tooltip>
            </template>
            <template v-slot:[`item.text`]="{ item }">
              <div class="py-3" v-html="item.text"></div>
            </template>
            <template v-slot:[`item.createdBy`]="{ item }">
              <template v-if="item.createdById">
                <p-contact-item
                  :contact="shortContactById(item.createdById)"
                  :avatar-class="'mr-2'"
                  avatar-only
                >
                </p-contact-item>
              </template>
              <template v-else>
                {{ item.createdBy }}
              </template>
            </template>
            <template v-slot:[`item.createdAt`]="{ item }">
              <div class="text-no-wrap">{{ formatDateTimeLocal(item.createdAt) }}</div>
            </template>
            <template v-slot:[`item.lastModifiedBy`]="{ item }">
              <template v-if="item.lastModifiedById">
                <p-contact-item
                  :contact="shortContactById(item.lastModifiedById)"
                  :avatar-class="'mr-2'"
                  avatar-only
                >
                </p-contact-item>
              </template>
              <template v-else>
                {{ item.lastModifiedBy }}
              </template>
            </template>
            <template v-slot:[`item.lastModifiedAt`]="{ item }">
              <div class="text-no-wrap">{{ formatDateTimeLocal(item.lastModifiedAt) }}</div>
            </template>
            <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
              <div class="p-table-row-actions p-visible-on-row-hover">
                <p-btn-icon type="edit" :tooltip="$t('rename')" @click="openEditDialog(item)"></p-btn-icon>
                <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
              </div>
            </template>
          </v-data-table>

          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="15"
            class="mt-5"
          ></v-pagination>
        </v-col>
<!--todo note.png-->
        <v-col v-else-if="notesLoaded">
          <v-divider class="mb-16"></v-divider>
          <p-no-data
            :text="$t('note.noNotes')"
            :btn-text="$t('note.createNote')"
            img="note.png"
            :hide-btn="!hasAccess()"
            @btn-click="openAddDialog"
          ></p-no-data>
        </v-col>
      </v-row>

      <note-create-dialog
        :dialog.sync="createDialog"
      ></note-create-dialog>

      <note-edit-dialog
        :note="editedNote"
        :dialog.sync="editDialog">
      </note-edit-dialog>

      <p-delete-dialog
        :dialog.sync="deleteDialog"
        :item="deletedNote"
        :item-data="deletedNoteData"
        :delete-item="deleteNote"
        :check-relations="false"
        @itemDeleted="onItemDeleted"
      ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import moment from 'moment'

import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'
import PContactItem from '@/components/PContactItem'

import NoteCreateDialog from './NoteCreateDialog'
import NoteEditDialog from './NoteEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSearchInput,
    PNoData,
    PBtnIcon,
    PDeleteDialog,
    PContactItem,
    NoteCreateDialog,
    NoteEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,
      createDialog: false,
      editDialog: false,
      editedNote: null,
      deleteDialog: false,
      deletedNote: null,
      deletedNoteData: null,
      notesLoaded: false,
      sendDialog: false,
      presetAttachments: null
    }
  },

  computed: {
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('note', ['notes', 'noteTitles']),
    ...mapGetters('contact', ['shortContactById']),
    ...mapGetters('changes', ['isItemChanged']),

    notesComputed () {
      return this.notes.map(note => {
        return {
          ...note,
          changed: this.isItemChanged(note.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('note.table.title'), value: 'title' },
        { text: '', value: 'changed' },
        { text: this.$t('note.table.text'), value: 'text', sortable: false, filterable: false },
        { text: this.$t('note.table.createdBy'), value: 'createdBy' },
        { text: this.$t('note.table.createdAt'), value: 'createdAt' },
        { text: this.$t('note.table.lastModifiedBy'), value: 'lastModifiedBy' },
        { text: this.$t('note.table.lastModifiedAt'), value: 'lastModifiedAt' },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    }
  },


  methods: {
    ...mapActions('note', ['loadNotes', 'getNote', 'deleteNote']),

    async searchNotes (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchNotes(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadNotes()
      })
    },

    setPagination (items) {
      this.pageCount = Math.ceil(items.itemsLength / this.itemsPerPage)
    },

    formatDateTimeLocal (date) {
      const localDate = moment.utc(date).local()
      return date ? moment(localDate).format('HH:mm, MMM D, yyyy') : ''
    },

    openAddDialog () {
      this.createDialog = true
    },

    openEditDialog (note) {
      this.editedNote = note
      this.editDialog = true
    },

    openDeleteDialog (note) {
      try {
        if (note) {
          this.deletedNote = note
          this.deletedNoteData = {
            type: this.$t('entity.note'),
            title: this.deletedNote.title
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('note.noteDeleted'))
    }
  },


  async created () {
    this.initEventBus()
    if (!this.currentProjectId) return false
    try {
      this.startPageLoading()
      await this.loadNotes()
      this.notesLoaded = true
      if (this.$router.currentRoute.params?.goto) {
        await this.searchNotes(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
